import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Input,
  Pagination,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { R1DataTable } from "../components/R1DataTable";
import { buildR1DownloadUrl, r1Api } from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";

type IssuerRow = Record<string, unknown>;

function FundTiles({ value }: { value: unknown }) {
  const funds: string[] = Array.isArray(value)
    ? (value as unknown[]).map(String)
    : typeof value === "string" && value
      ? value.split(", ")
      : [];
  if (funds.length === 0) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1 py-0.5">
      {funds.map((name, i) => (
        <span
          key={i}
          className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600"
        >
          {name}
        </span>
      ))}
    </div>
  );
}

function orderedKeys(row: IssuerRow): string[] {
  const keys = Object.keys(row).filter((k) => k !== "fund_count");
  const insertAfter = "brand_name";
  const idx = keys.indexOf(insertAfter);
  if (idx === -1 || !keys.includes("fund_names")) return keys;
  const without = keys.filter((k) => k !== "fund_names");
  without.splice(idx + 1, 0, "fund_names");
  return without;
}

function buildColumns(row: IssuerRow): ColumnDef<IssuerRow>[] {
  return orderedKeys(row)
    .map((key) => ({
      accessorKey: key,
      header: key === "fund_names" ? "Funds" : key,
      size: key === "fund_names" ? 320 : 140,
      cell: ({ getValue }: { getValue: () => unknown }) => {
        const v = getValue();
        if (key === "fund_names") return <FundTiles value={v} />;
        if (v === null || v === undefined) return "";
        if (typeof v === "boolean") return v ? "Yes" : "No";
        if (typeof v === "object") return JSON.stringify(v);
        return String(v);
      },
    }));
}

type SortOption = "name_asc" | "name_desc" | "id_asc" | "id_desc";

const SORT_OPTIONS: { key: SortOption; label: string }[] = [
  { key: "name_asc",  label: "A → Z" },
  { key: "name_desc", label: "Z → A" },
  { key: "id_asc",   label: "ID ↑" },
  { key: "id_desc",  label: "ID ↓" },
];

interface IssuerSearch {
  id: string;
  name: string;
  gpId: string;
  regNumber: string;
  country: string;
}

const EMPTY_SEARCH: IssuerSearch = {
  id: "",
  name: "",
  gpId: "",
  regNumber: "",
  country: "",
};

function SearchPopover({
  search,
  onChange,
}: {
  search: IssuerSearch;
  onChange: (s: IssuerSearch) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [local, setLocal] = useState<IssuerSearch>(search);
  const hasFilter = Object.values(search).some(Boolean);

  const submit = () => onChange(local);
  const clear = () => { setLocal(EMPTY_SEARCH); onChange(EMPTY_SEARCH); };
  const onKey = (e: React.KeyboardEvent) => { if (e.key === "Enter") submit(); };

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement="bottom-end" showArrow offset={10}>
      <PopoverTrigger>
        <Button color={hasFilter ? "secondary" : "primary"} size="sm" radius="full">
          Search
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="flex flex-col gap-3 p-3 w-72" onKeyDown={onKey}>
          <Input size="sm" label="ID" placeholder="Issuer ID exact…"
            value={local.id} onValueChange={(v) => setLocal((s) => ({ ...s, id: v }))} />
          <Input size="sm" label="Name" placeholder="Company name, brand, eFront…"
            value={local.name} onValueChange={(v) => setLocal((s) => ({ ...s, name: v }))} />
          <Input size="sm" label="GP ID" placeholder="GP ID (PC)…"
            value={local.gpId} onValueChange={(v) => setLocal((s) => ({ ...s, gpId: v }))} />
          <Input size="sm" label="Reg. Number" placeholder="Commercial register…"
            value={local.regNumber} onValueChange={(v) => setLocal((s) => ({ ...s, regNumber: v }))} />
          <Input size="sm" label="Country" placeholder="Country of domicile…"
            value={local.country} onValueChange={(v) => setLocal((s) => ({ ...s, country: v }))} />
          <div className="flex gap-2">
            <Button size="sm" color="primary" className="flex-1" onPress={submit}>Search</Button>
            {hasFilter && (
              <Button size="sm" variant="flat" onPress={clear}>Clear</Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const PAGE_SIZE = 100;

export default function IssuersView() {
  const [search, setSearch] = useState<IssuerSearch>(EMPTY_SEARCH);
  const [currentPage, setCurrentPage] = useState(1);
  const [gpIdEmpty, setGpIdEmpty] = useState(false);
  const [multiFund, setMultiFund] = useState(false);
  const [noFund, setNoFund] = useState(false);
  const [sort, setSort] = useState<SortOption>("name_asc");

  const handleSearchChange = (s: IssuerSearch) => {
    setSearch(s);
    setCurrentPage(1);
  };

  const sortBy = sort.startsWith("id") ? "id" : "name";
  const sortDir = sort.endsWith("asc") ? "asc" : "desc";

  const toggleFilter = (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    setter((prev) => !prev);
    setCurrentPage(1);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["r1", "issuers-view", currentPage, search, gpIdEmpty, multiFund, noFund, sort],
    queryFn: () =>
      r1Api.getIssuersView({
        page: currentPage,
        pageSize: PAGE_SIZE,
        idEquals: search.id && /^\d+$/.test(search.id.trim()) ? Number(search.id) : undefined,
        name: search.name || undefined,
        gpId: search.gpId || undefined,
        regNumber: search.regNumber || undefined,
        country: search.country || undefined,
        gpIdEmpty: gpIdEmpty || undefined,
        multiFund: multiFund || undefined,
        noFund: noFund || undefined,
        sortBy,
        sortDir,
      }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const rows = (data?.data ?? []) as IssuerRow[];
  const totalCount = data?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const columns = useMemo(
    () => (rows[0] ? buildColumns(rows[0]) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [!!rows[0]],
  );

  const hasAnyFilter =
    Object.values(search).some(Boolean) || gpIdEmpty || multiFund;

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-3">
              <h1 className="font-semibold text-xl text-primary">Issuers</h1>
              <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
                {totalCount.toLocaleString()} issuers
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleFilter(setGpIdEmpty)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                  gpIdEmpty
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                }`}
              >
                Empty GP-ID
              </button>
              <button
                onClick={() => toggleFilter(setMultiFund)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                  multiFund
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                }`}
              >
                In several funds
              </button>
              <button
                onClick={() => toggleFilter(setNoFund)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                  noFund
                    ? "bg-red-500 text-white border-red-500"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                }`}
              >
                No fund
              </button>
              <div className="shrink-0 w-32">
                <Select
                  size="sm"
                  aria-label="Sort"
                  selectedKeys={new Set([sort])}
                  onSelectionChange={(keys) => {
                    const val = (keys === "all" ? null : Array.from(keys)[0]) as SortOption | null;
                    if (val) { setSort(val); setCurrentPage(1); }
                  }}
                  classNames={{
                    trigger: "h-7 min-h-7 bg-white shadow-none border border-gray-200 text-[11px] rounded-full",
                    value: "text-[11px] text-gray-600",
                    base: "h-7",
                  }}
                >
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.key}>{o.label}</SelectItem>
                  ))}
                </Select>
              </div>
              <SearchPopover search={search} onChange={handleSearchChange} />
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
            <R1DataTable<IssuerRow, unknown>
              table_name="r1-issuers"
              columns={columns}
              data={rows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["company_name_long"]}
              noCard
            />
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildR1DownloadUrl("r1_issuers", new URLSearchParams())}
                filename={`r1_issuers_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
              <div className="flex items-center gap-4">
                {hasAnyFilter && (
                  <span className="text-[11px] text-gray-400 italic">
                    Filters active
                  </span>
                )}
                <span className="text-sm text-slate-500">
                  {totalCount.toLocaleString()} issuers
                </span>
                {pageCount > 1 && (
                  <Pagination
                    showControls
                    total={pageCount}
                    page={currentPage}
                    onChange={setCurrentPage}
                    size="sm"
                    radius="md"
                    color="primary"
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
